import type { FC } from 'react'
import { useEffect, useRef, useState } from 'react'

import { InfoBox, LoaderCircle, MessagePopup, Popup, Toggle } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { ONE_TIME_ORDER_INTERVAL } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import { twJoin } from 'tailwind-merge'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { FormattedNumberWithCurrency } from '@/components/FormattedNumberWithCurrency'
import { useIsAdmin } from '@/hooks/access'
import { commonEcomPolicyResources } from '@/resources/ecom-policy'
import { commonQueries } from '@/resources/queries'
import { numberFormat } from '@/util/formatters/numbers'

import ConfirmationMessage from './ConfirmationMessage'
import { EcomPolicyCheckbox } from './EcomPolicyCheckbox'
import {
  getAlteredPolicyIds,
  getCostObjectIdsByPolicy,
  getAffectedCostObjectsIdsByPolicyId,
  getPolicyStatus,
  getUpdatedPoliciesByCostObjectId,
  getUpdatedTelePoliciesByCostObjectId,
} from './helpers'

import queries from '../../queries'
import adminResources from '../../resources'
import type { CostObject } from '../../resources'

import './index.styles.scss'

const AssignPoliciesPopup: FC<{
  costObjects: CostObject[]
  onClose: () => void
}> = ({ costObjects, onClose }) => {
  const isAdmin = useIsAdmin()
  const translate = useTranslation()
  const isEdited = useRef(false)
  const [ecomPoliciesByCostObjectId, setEcomPoliciesByCostObjectId] = useState<
    { costObjectId: number; policyIds: number[] | null }[]
  >([])
  const [telePoliciesByCostObjectId, setTelePoliciesByCostObjectId] = useState<
    { costObjectId: number; policyIds: number[] | null }[]
  >([])
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false)

  const { data: availableEcomPolicies, isLoading: isEcomPoliciesLoading } = useQuery(
    commonQueries.policyList(),
    commonEcomPolicyResources.getList
  )
  const activePolicies = availableEcomPolicies?.filter(policy => policy.isActive) || []
  const { data: availableTelePolicies = [], isLoading: isTelePoliciesLoading } = useQuery(
    queries.adminTelePoliciesList(),
    adminResources.getTelePolicyList,
    {
      enabled: isAdmin,
    }
  )

  function onPopupClose() {
    const ecomPolicies =
      costObjects?.map(costObject => ({
        costObjectId: costObject.id,
        policyIds: (costObject.policyIds || '').split(',').map(Number),
      })) || []
    const telePolicies =
      costObjects?.map(costObject => ({
        costObjectId: costObject.id,
        policyIds: costObject.telePolicyId ? [costObject.telePolicyId] : null,
      })) || []
    setEcomPoliciesByCostObjectId(ecomPolicies)
    setEcomPoliciesByCostObjectId(telePolicies)
    onClose()
  }

  useEffect(() => {
    const ecomPolicies =
      costObjects?.map(costObject => ({
        costObjectId: costObject.id,
        policyIds: costObject.policyIds?.split(',').map(Number) || null,
      })) || []
    setEcomPoliciesByCostObjectId(ecomPolicies)
    const telePolicies =
      costObjects?.map(costObject => ({
        costObjectId: costObject.id,
        policyIds: costObject.telePolicyId ? [costObject.telePolicyId] : null,
      })) || []
    setTelePoliciesByCostObjectId(telePolicies)
  }, [costObjects])

  const telePolicies =
    costObjects?.map(costObject => ({
      costObjectId: costObject.id,
      policyIds: costObject.telePolicyId ? [costObject.telePolicyId] : null,
    })) || []
  const policies =
    costObjects?.map(costObject => ({
      costObjectId: costObject.id,
      policyIds: costObject.policyIds?.split(',').map(Number) || null,
    })) || []
  const currEcom = getCostObjectIdsByPolicy(ecomPoliciesByCostObjectId)
  const initEcom = getCostObjectIdsByPolicy(policies)
  const currTele = getCostObjectIdsByPolicy(telePoliciesByCostObjectId)
  const initTele = getCostObjectIdsByPolicy(telePolicies)
  const alteredEcomPolicyIds = getAlteredPolicyIds({
    init: initEcom,
    curr: currEcom,
    policyIds: availableEcomPolicies?.map(policy => policy.id) || [],
  })
  const alteredTelePolicyIds = getAlteredPolicyIds({
    init: initTele,
    curr: currTele,
    policyIds: availableTelePolicies?.map(policy => policy.id) || [],
  })
  const affectedCostObjectsByEcomPolicy = getAffectedCostObjectsIdsByPolicyId({
    initCostObjectList: policies,
    currentCostObjectList: ecomPoliciesByCostObjectId,
  })
  const affectedCostObjectsByTelePolicy = getAffectedCostObjectsIdsByPolicyId({
    initCostObjectList: telePolicies,
    currentCostObjectList: telePoliciesByCostObjectId,
  })

  const isLoading =
    isEcomPoliciesLoading || isTelePoliciesLoading || ecomPoliciesByCostObjectId?.length === 0

  return (
    <Popup
      className="assign-policies-popup"
      ariaLabel={translate('Assign policies')}
      isOpen={true}
      onClose={onPopupClose}
    >
      {isLoading ? (
        <LoaderCircle />
      ) : (
        <>
          <InfoBox emphasis="warning" marginRight={4} marginBottom={4}>
            {isEdited.current ? (
              <Translation
                id="The introduced changes affect {numberOfUser} user(s)"
                numberOfUser={String(
                  affectedCostObjectsByEcomPolicy.numberOfAllAffectedCostObjects +
                    affectedCostObjectsByTelePolicy.numberOfAllAffectedCostObjects
                )}
              />
            ) : (
              <Translation id="No changes introduced yet" />
            )}
          </InfoBox>

          <h2>
            <Translation id="Ecom policies" />
          </h2>

          <table className="policy-filter-table mb-5">
            <thead>
              <tr>
                <th />
                <th>
                  <Translation id="Name" />
                </th>
                <th className="is-mobile-hidden text-right">
                  <Translation id="Cover amount" />
                </th>
                <th colSpan={2} className="center" />
              </tr>
            </thead>

            <tbody>
              {activePolicies?.map(policy => {
                const status = getPolicyStatus({
                  policyId: policy.id,
                  costObjectList: ecomPoliciesByCostObjectId,
                })
                const isPolicyAltered =
                  alteredEcomPolicyIds.removed.includes(policy.id) ||
                  alteredEcomPolicyIds.assigned.includes(policy.id)

                return (
                  <tr
                    key={policy.id}
                    // todo: remove important then styling is refactored
                    className={twJoin('policy-subscription', isPolicyAltered && '!bg-orange-50')}
                  >
                    <td className="checkbox-cell">
                      <EcomPolicyCheckbox
                        status={status}
                        policyId={policy.id}
                        onClick={() => {
                          const updatedTelePoliciesByCostObjectId =
                            getUpdatedPoliciesByCostObjectId({
                              policyId: policy.id,
                              costObjectList: ecomPoliciesByCostObjectId,
                              policyStatus: status,
                            })
                          if (!isEdited.current) isEdited.current = true
                          setEcomPoliciesByCostObjectId(updatedTelePoliciesByCostObjectId)
                        }}
                      />
                    </td>
                    <td>
                      <div>{policy.name}</div>

                      <div>
                        <small>{policy.vendorName}</small>
                      </div>
                      <div>
                        <small>{policy.description}</small>
                      </div>
                    </td>

                    <td className="is-mobile-hidden text-right">
                      <div>
                        <FormattedNumberWithCurrency amount={policy.coverAmount || 0} />
                      </div>
                      <div>
                        {policy.orderInterval === ONE_TIME_ORDER_INTERVAL ? (
                          <small>
                            <Translation id="One-time order" />
                          </small>
                        ) : (
                          <small>
                            {numberFormat(policy.orderInterval)} <Translation id="mos" />
                          </small>
                        )}
                      </div>
                    </td>
                    <td className="is-mobile-hidden" />
                  </tr>
                )
              })}
            </tbody>
          </table>

          <h2>
            <Translation id="Tele policies" />
          </h2>

          <table className="policy-filter-table">
            <thead>
              <tr>
                <th />
                <th>
                  <Translation id="Name" />
                </th>
                <th className="is-mobile-hidden">
                  <Translation id="Template" />
                </th>
                <th colSpan={2} className="center" />
              </tr>
            </thead>

            <tbody>
              {availableTelePolicies?.map(policy => {
                const status = getPolicyStatus({
                  policyId: policy.id,
                  costObjectList: telePoliciesByCostObjectId,
                })
                const isPolicyAltered =
                  alteredTelePolicyIds.removed.includes(policy.id) ||
                  alteredTelePolicyIds.assigned.includes(policy.id)

                return (
                  <tr
                    key={policy.id}
                    // todo: remove important then styling is refactored
                    className={twJoin('policy-subscription', isPolicyAltered && '!bg-orange-50')}
                  >
                    <td>
                      <Toggle
                        id={`tele-policy-id-${policy.id}`}
                        label=" "
                        checked={status === 'not_assigned' ? false : true}
                        onChange={() => {
                          const updatedPoliciesByCostObjectId =
                            getUpdatedTelePoliciesByCostObjectId({
                              policyId: policy.id,
                              costObjectList: telePoliciesByCostObjectId,
                              policyStatus: status,
                            })
                          if (!isEdited.current) isEdited.current = true
                          setTelePoliciesByCostObjectId(updatedPoliciesByCostObjectId)
                        }}
                      />
                    </td>
                    <td>{policy.name}</td>
                    <td>
                      {policy.telePolicyTemplate && (
                        <div>
                          <small>
                            <Translation id="Template:" /> <b>{policy.telePolicyTemplate.name}</b>
                          </small>
                        </div>
                      )}
                    </td>
                    <td className="is-mobile-hidden" />
                  </tr>
                )
              })}
            </tbody>
          </table>
        </>
      )}

      <footer>
        <Button variant="tertiary" onClick={onPopupClose}>
          <Translation id="Cancel" />
        </Button>
        <Button
          variant="primary"
          disabled={!isEdited.current}
          onClick={() => {
            setIsConfirmationModalOpen(true)
          }}
        >
          <Translation id="Apply" />
        </Button>
      </footer>

      <MessagePopup
        className="assign-policies-confirmation-popup"
        isOpen={isConfirmationModalOpen}
        title={<Translation id="Assign policies confirmation" />}
        type="warning"
        onClose={() => setIsConfirmationModalOpen(false)}
      >
        <ConfirmationMessage
          affectedCostObjectsByEcomPolicy={affectedCostObjectsByEcomPolicy}
          affectedCostObjectsByTelePolicy={affectedCostObjectsByTelePolicy}
          alteredTelePolicyIds={alteredTelePolicyIds}
          alteredEcomPolicyIds={alteredEcomPolicyIds}
          costObjects={costObjects}
          onCancel={() => setIsConfirmationModalOpen(false)}
          onClose={() => {
            setIsConfirmationModalOpen(false)
            onClose()
          }}
        />
      </MessagePopup>
    </Popup>
  )
}

export default AssignPoliciesPopup
